import React from "react";
import { defineMessage, useIntl } from "react-intl";
import { Link, useLocation } from "react-router-dom";

import {
  Home24,
  ModeOfTravel24,
  MotionSensorActive24,
  NotificationsActive24,
  Pallet24,
} from "@/components/ui/SvgIcon";

import cn from "@/utils/cn";
import { ROUTES } from "@/constants/routes";

export const MobileNav = () => {
  const intl = useIntl();
  const pathname = useLocation().pathname;

  return (
    <nav className="fixed inset-x-0 bottom-0 z-10 flex justify-around rounded-t-xl bg-white p-2 shadow-mobile-nav md:hidden">
      <MobileNavItemLink
        to={ROUTES.HOME}
        icon={<Home24 />}
        name={intl.formatMessage(
          defineMessage({
            defaultMessage: "Home",
            description:
              "Navigation link label in sidebar for accessing home page.",
          }),
        )}
      />

      <MobileNavItemLink
        to={ROUTES.ASSETS}
        icon={<Pallet24 />}
        name={intl.formatMessage(
          defineMessage({
            defaultMessage: "Assets",
            description:
              "Navigation link label in sidebar for accessing customer assets. The term 'assets' refers to physical goods, ranging from trucks full of produce to pharmaceuticals or construction equipment",
          }),
        )}
        isCurrent={pathname.includes(ROUTES.ASSETS)}
      />

      <MobileNavItemLink
        to={ROUTES.TRIPS}
        icon={<ModeOfTravel24 />}
        name={intl.formatMessage(
          defineMessage({
            defaultMessage: "Trips",
            description:
              "Navigation link label in sidebar for accessing trips. A 'trip' is a scheduled shipment of, e.g., a package between two countries",
          }),
        )}
        isCurrent={pathname.includes(ROUTES.TRIPS)}
      />

      <MobileNavItemLink
        to={ROUTES.ALERTS}
        icon={<NotificationsActive24 />}
        name={intl.formatMessage(
          defineMessage({
            defaultMessage: "Alerts",
            description:
              "Navigation link label in sidebar for accessing alerts. Alerts can be configured by the user to detect when, for example, their refrigerator is too warm or a scheduled shipment arrives late",
          }),
        )}
        isCurrent={pathname.includes(ROUTES.ALERTS)}
      />

      <MobileNavItemLink
        to={ROUTES.SYSTEM}
        icon={<MotionSensorActive24 />}
        name={intl.formatMessage(
          defineMessage({
            defaultMessage: "System",
            description:
              "Navigation link label in sidebar for accessing sensors. Sensors refers to tracking sensors which have GPS to allow them to be tracked across the world",
          }),
        )}
        isCurrent={pathname.includes(ROUTES.SYSTEM)}
      />
    </nav>
  );
};

type Props = {
  to: string;
  name: string;
  icon?: React.ReactElement;
  isCurrent?: boolean;
  count?: number;
};
const MobileNavItemLink = ({ to, name, icon, isCurrent, count }: Props) => {
  return (
    <Link
      to={to}
      className={cn(
        "flex h-14 w-16 flex-col items-center justify-center text-xs font-medium",
        isCurrent ? "text-chorus-black-100" : "text-chorus-gray-900",
      )}
    >
      {icon &&
        React.cloneElement(icon, {
          className: isCurrent
            ? "fill-chorus-teal-500"
            : "fill-chorus-gray-500",
        })}
      <span className="-mt-0.5">{name}</span>
      {Boolean(count) && (
        <span className="ml-auto inline-flex h-5 min-w-[30px] items-center justify-center rounded-[20px] bg-chorus-red-600 px-2 text-xxs font-semibold text-chorus-red-100">
          {count}
        </span>
      )}
    </Link>
  );
};
